import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  Briefcase,
  Calendar,
  CheckSquare,
  Clock,
  Coins,
  FileText,
  Loader2,
  Receipt,
  Scale,
  Timer,
  TrendingUp,
} from 'lucide-react'
import { api } from '../lib/api'
import { formatCents } from '../lib/money'
import UpcomingEventsWidget from '../components/calendar/UpcomingEventsWidget'
import BarChart from '../components/charts/BarChart'
import DonutChart from '../components/charts/DonutChart'
import {
  type CasesBreakdownResponse,
  type DashboardKpi,
  type IssuerDto,
  type MonthlyBillingResponse,
  type TopClientsResponse,
} from '@toga/shared'
import { useAuth } from '../contexts/AuthContext'
import OnboardingBanner from '../components/OnboardingBanner'
import VerifactuAlertsBanner from '../components/billing/VerifactuAlertsBanner'
import PendingBillingCard from '../components/billing/PendingBillingCard'

export default function Dashboard() {
  const auth = useAuth()
  const userName = auth.status === 'authenticated' ? auth.user.fullName : ''
  const [kpi, setKpi] = useState<DashboardKpi | null>(null)
  const [monthly, setMonthly] = useState<MonthlyBillingResponse | null>(null)
  const [cases, setCases] = useState<CasesBreakdownResponse | null>(null)
  const [topClients, setTopClients] = useState<TopClientsResponse | null>(null)
  const [issuers, setIssuers] = useState<IssuerDto[]>([])
  const [issuerId, setIssuerId] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<{ items: IssuerDto[] }>('/issuers')
      .then((r) => setIssuers(r.items))
      .catch(() => setIssuers([]))
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const issuerQs = issuerId ? `&issuerId=${issuerId}` : ''
      const [k, m, c, t] = await Promise.all([
        api.get<DashboardKpi>(`/stats/dashboard?${issuerQs.slice(1)}`),
        api.get<MonthlyBillingResponse>(`/stats/billing-monthly?months=12${issuerQs}`),
        api.get<CasesBreakdownResponse>('/stats/cases-breakdown'),
        api.get<TopClientsResponse>(`/stats/top-clients?limit=8${issuerQs}`),
      ])
      setKpi(k)
      setMonthly(m)
      setCases(c)
      setTopClients(t)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar')
    } finally {
      setLoading(false)
    }
  }, [issuerId])

  useEffect(() => {
    void load()
  }, [load])

  const greeting = useMemo(() => {
    const h = new Date().getHours()
    if (h < 14) return 'Buenos días'
    if (h < 21) return 'Buenas tardes'
    return 'Buenas noches'
  }, [])

  return (
    <div className="space-y-6 animate-slideUp">
      <OnboardingBanner />
      <VerifactuAlertsBanner />
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-serif font-semibold t-primary">
            {greeting}, <span className="text-toga-300">{userName.split(' ')[0]}</span>
          </h1>
          <p className="t-muted text-sm">
            Resumen general del bufete ·{' '}
            {new Date().toLocaleDateString('es-ES', { dateStyle: 'full' })}
          </p>
        </div>
        {issuers.length > 1 && (
          <label className="text-xs t-muted flex items-center gap-2">
            Facturación de
            <select
              value={issuerId}
              onChange={(e) => setIssuerId(e.target.value)}
              className="bg-transparent border border-base rounded px-2 py-1 text-sm t-secondary focus:outline-none focus:border-toga-500/50"
            >
              <option value="">Global (todos)</option>
              {issuers.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </header>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {loading && !kpi ? (
        <div className="t-muted flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Cargando…
        </div>
      ) : (
        kpi && (
          <>
            {/* F16: lo accionable primero — tareas, plazos y agenda por encima
                de la facturación. */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MiniKpi
                icon={CheckSquare}
                label="Mis tareas"
                value={`${kpi.myOpenTasksCount} / ${kpi.openTasksCount}`}
                href="/tasks"
                hint="abiertas"
              />
              <MiniKpi
                icon={AlertTriangle}
                label="Plazos vencidos"
                value={String(kpi.overdueDeadlines)}
                href="/deadlines"
                alert={kpi.overdueDeadlines > 0}
                tone="rose"
              />
              <MiniKpi
                icon={Clock}
                label="Plazos próximos 7d"
                value={String(kpi.deadlinesNext7d)}
                href="/deadlines"
                alert={kpi.deadlinesNext7d > 0}
              />
              <MiniKpi
                icon={Timer}
                label="Tiempos sin facturar"
                value={formatMinutes(kpi.unbilledTimeMinutes)}
                hint={formatCents(kpi.unbilledTimeAmountCents)}
              />
            </section>

            {/* Próximos eventos + atajos */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="surface p-5">
                <h3 className="t-primary font-semibold flex items-center gap-2 mb-3">
                  <Calendar size={14} className="text-toga-300" />
                  Próximos eventos
                </h3>
                <UpcomingEventsWidget />
              </div>
              <div className="surface p-5">
                <h3 className="t-primary font-semibold flex items-center gap-2 mb-3">
                  <FileText size={14} className="text-toga-300" />
                  Atajos rápidos
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <QuickLink to="/billing/new" label="Nueva factura" icon={Receipt} />
                  <QuickLink to="/cases" label="Expedientes" icon={Scale} />
                  <QuickLink to="/tasks" label="Mis tareas" icon={CheckSquare} />
                  <QuickLink to="/agenda" label="Calendario" icon={Calendar} />
                  <QuickLink to="/ai" label="Asistente IA" icon={TrendingUp} />
                  <QuickLink to="/deadlines" label="Plazos" icon={Clock} />
                </div>
              </div>
            </section>

            {/* KPIs de facturación */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KpiCard
                icon={TrendingUp}
                label="Facturado este mes"
                value={formatCents(kpi.billingThisMonthCents)}
                hint={`Año: ${formatCents(kpi.billingThisYearCents)}`}
                accent="bg-toga-500/15 text-toga-300"
              />
              <KpiCard
                icon={Receipt}
                label="Pendiente cobro"
                value={formatCents(kpi.pendingTotalCents)}
                hint={
                  kpi.overduePendingCents > 0
                    ? `${formatCents(kpi.overduePendingCents)} vencido`
                    : 'Sin vencidos'
                }
                accent={
                  kpi.overduePendingCents > 0
                    ? 'bg-rose-500/15 text-rose-300'
                    : 'bg-amber-500/15 text-amber-300'
                }
                hintTone={kpi.overduePendingCents > 0 ? 'rose' : 'muted'}
              />
              <KpiCard
                icon={Scale}
                label="Expedientes activos"
                value={String(kpi.activeCasesCount)}
                hint="Sin archivar"
                accent="bg-blue-500/15 text-blue-300"
              />
              <KpiCard
                icon={Coins}
                label="Provisiones disponibles"
                value={formatCents(kpi.fundsAvailableTotalCents)}
                hint="Saldo total clientes"
                accent="bg-emerald-500/15 text-emerald-300"
              />
            </section>

            <PendingBillingCard />

            {/* Gráficos principales */}
            <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="surface p-5 lg:col-span-2">
                <h3 className="t-primary font-semibold flex items-center gap-2 mb-3">
                  <TrendingUp size={14} className="text-toga-300" />
                  Facturación últimos 12 meses
                  <span className="text-[10px] t-faint font-normal flex items-center gap-2 ml-auto">
                    <span className="inline-block w-2 h-2 rounded-sm bg-[#a31b41]" /> Facturado
                    <span className="inline-block w-2 h-2 rounded-sm bg-emerald-500" /> Cobrado
                  </span>
                </h3>
                {monthly && (
                  <BarChart
                    data={monthly.points.map((p) => ({
                      label: monthLabel(p.month),
                      value: p.totalCents,
                      secondary: p.paidCents,
                    }))}
                    formatValue={(n) => formatCents(n)}
                    showSecondary
                    height={240}
                  />
                )}
              </div>

              <div className="surface p-5">
                <h3 className="t-primary font-semibold flex items-center gap-2 mb-3">
                  <Scale size={14} className="text-toga-300" />
                  Expedientes por estado
                </h3>
                {cases && cases.byStatus.length > 0 ? (
                  <DonutChart
                    data={cases.byStatus.map((b) => ({ label: b.label, value: b.count }))}
                  />
                ) : (
                  <p className="text-xs t-faint">Sin expedientes todavía.</p>
                )}
              </div>
            </section>

            <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="surface p-5">
                <h3 className="t-primary font-semibold flex items-center gap-2 mb-3">
                  <Briefcase size={14} className="text-toga-300" />
                  Expedientes por área
                </h3>
                {cases && cases.byArea.length > 0 ? (
                  <DonutChart
                    data={cases.byArea.map((b) => ({ label: b.label, value: b.count }))}
                  />
                ) : (
                  <p className="text-xs t-faint">Sin datos.</p>
                )}
              </div>

              <div className="surface p-5 lg:col-span-2">
                <h3 className="t-primary font-semibold flex items-center gap-2 mb-3">
                  <Receipt size={14} className="text-toga-300" />
                  Top clientes {topClients?.year ?? new Date().getFullYear()}
                </h3>
                {topClients && topClients.items.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase tracking-wider t-faint">
                      <tr>
                        <th className="text-left py-1">Cliente</th>
                        <th className="text-right">Facturado</th>
                        <th className="text-right">Cobrado</th>
                        <th className="text-right">Facturas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topClients.items.map((c) => (
                        <tr key={c.clientId} className="border-t border-base/40">
                          <td className="py-1.5 t-primary truncate">{c.clientDisplayName}</td>
                          <td className="py-1.5 text-right font-mono t-secondary">
                            {formatCents(c.totalCents)}
                          </td>
                          <td className="py-1.5 text-right font-mono text-emerald-300">
                            {formatCents(c.paidCents)}
                          </td>
                          <td className="py-1.5 text-right t-faint">{c.invoicesCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-xs t-faint">Sin facturas emitidas este año.</p>
                )}
              </div>
            </section>
          </>
        )
      )}
    </div>
  )
}

function KpiCard({
  icon: Icon,
  label,
  value,
  hint,
  accent,
  hintTone,
}: {
  icon: typeof Receipt
  label: string
  value: string
  hint: string
  accent: string
  hintTone?: 'rose' | 'muted'
}) {
  return (
    <div className="surface p-5 flex items-start gap-4">
      <div className={`w-12 h-12 rounded-xl grid place-items-center ${accent}`}>
        <Icon size={22} />
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wider t-muted">{label}</div>
        <div className="text-2xl font-semibold t-primary mt-1 font-mono">{value}</div>
        <div className={`text-xs mt-1 ${hintTone === 'rose' ? 'text-rose-300' : 't-faint'}`}>
          {hint}
        </div>
      </div>
    </div>
  )
}

function MiniKpi({
  icon: Icon,
  label,
  value,
  hint,
  href,
  alert,
  tone,
}: {
  icon: typeof Clock
  label: string
  value: string
  hint?: string
  href?: string
  alert?: boolean
  tone?: 'rose'
}) {
  const cls = [
    'surface p-3 flex items-center gap-3 transition',
    href ? 'hover:bg-hover cursor-pointer' : '',
    alert && tone === 'rose' ? 'border-rose-500/40' : '',
    alert ? 'ring-1 ring-amber-500/30' : '',
  ].join(' ')
  const inner = (
    <>
      <Icon size={18} className={tone === 'rose' ? 'text-rose-300' : 'text-toga-300'} />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider t-faint">{label}</div>
        <div className="text-sm font-semibold t-primary">{value}</div>
        {hint && <div className="text-[10px] t-faint">{hint}</div>}
      </div>
    </>
  )
  return href ? (
    <Link to={href} className={cls}>
      {inner}
    </Link>
  ) : (
    <div className={cls}>{inner}</div>
  )
}

function QuickLink({ to, label, icon: Icon }: { to: string; label: string; icon: typeof Receipt }) {
  return (
    <Link
      to={to}
      className="px-3 py-2 rounded-lg border border-base hover:bg-hover hover:border-toga-500/40 text-sm t-secondary flex items-center gap-2"
    >
      <Icon size={14} className="text-toga-300" />
      {label}
    </Link>
  )
}

function monthLabel(yyyymm: string): string {
  const [year, month] = yyyymm.split('-')
  const m = Number(month) - 1
  const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
  return `${meses[m]}'${(year ?? '').slice(2)}`
}

function formatMinutes(m: number): string {
  const h = Math.floor(m / 60)
  const rem = m % 60
  return `${h}h ${String(rem).padStart(2, '0')}m`
}
